import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { downloadProfileFlyer, svgElementToDataUri } from '@/features/profile/profileShareFlyer';
import { buildProfileShareUrl } from '@/features/profile/profileShareUtils';
import { MOCK_CURRENT_USER_PROFILE } from '@/features/profile/mockData';
import { useProfileShareStore } from '@/features/profile/profileShareStore';
import Modal from '@/shared/ui/Modal';
import ModalHeader from '@/shared/ui/ModalHeader';
import Button from '@/shared/ui/Button';
import Avatar from '@/shared/ui/Avatar';
import { CopyIcon, DownloadIcon } from '@/shared/icons';
import { getInitials } from '@/lib/getInitials';
import { pickMediaUrl } from '@/lib/pickMediaUrl';
import { useAuthStore, type RawAuthUser } from '@/store/authStore';
import { cn } from '@/shared/lib/cn';
import { textStyles } from '@/shared/tokens/typography';

interface ProfileShareModalProps {
  onClose: () => void;
}

const SHARE_AVATAR_CLASS =
  'h-24 w-24 rounded-full border-4 border-white object-cover shadow-sm ring-1 ring-gray-200';

function pickString(
  source: Record<string, unknown> | null | undefined,
  ...keys: string[]
): string | undefined {
  if (!source) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number') return String(value);
  }
  return undefined;
}

function getLoggedUserSchoolName(
  authUser: { schoolName?: string } | null,
  rawUser: RawAuthUser | null,
): string {
  const university = rawUser?.university as Record<string, unknown> | undefined;
  return (
    authUser?.schoolName?.trim() ||
    pickString(university, 'name', 'abbreviation') ||
    pickString(
      rawUser,
      'schoolName',
      'school_name',
      'institution',
      'organisation',
      'universityName',
    ) ||
    MOCK_CURRENT_USER_PROFILE.institution
  );
}

export default function ProfileShareModal({ onClose }: ProfileShareModalProps) {
  const { t } = useTranslation('profile');
  const authUser = useAuthStore((state) => state.user);
  const rawUser = useAuthStore((state) => state.rawUser);
  const shareTarget = useProfileShareStore((state) => state.target);
  const qrContainerRef = useRef<HTMLDivElement | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [flyerDownloaded, setFlyerDownloaded] = useState(false);

  const profile = MOCK_CURRENT_USER_PROFILE;
  const displayName = shareTarget?.name ?? authUser?.name ?? profile.name;
  const displayInitials = shareTarget?.initials ?? getInitials(displayName);
  const avatarUrl = shareTarget
    ? shareTarget.avatarUrl
    : (pickMediaUrl(
        rawUser,
        'profileImageThumbnail',
        'profileImage',
        'avatarUrl',
        'profilePic',
        'photo',
        'profilePicture',
        'imageUrl',
        'image',
      ) ??
      authUser?.avatarUrl ??
      profile.avatarUrl);
  const school = shareTarget?.school?.trim() || getLoggedUserSchoolName(authUser, rawUser);
  const profileIdentifier = shareTarget?.profileIdentifier ?? authUser?.id ?? profile.username;

  const shareUrl = buildProfileShareUrl(profileIdentifier);

  const flashFeedback = useCallback((setter: (value: boolean) => void) => {
    setter(true);
    window.setTimeout(() => setter(false), 2000);
  }, []);

  const getQrDataUri = useCallback((): string | undefined => {
    const svg = qrContainerRef.current?.querySelector('svg');
    if (!svg) {
      return undefined;
    }
    return svgElementToDataUri(svg);
  }, []);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      flashFeedback(setLinkCopied);
    } catch {
      setLinkCopied(false);
    }
  }, [flashFeedback, shareUrl]);

  const handleDownloadFlyer = useCallback(async () => {
    try {
      await downloadProfileFlyer({
        name: displayName,
        school,
        shareUrl,
        qrDataUri: getQrDataUri(),
      });
      flashFeedback(setFlyerDownloaded);
    } catch {
      setFlyerDownloaded(false);
    }
  }, [displayName, flashFeedback, getQrDataUri, school, shareUrl]);

  return (
    <Modal
      onClose={onClose}
      zIndexClassName="z-[100]"
      className="flex w-full max-w-lg flex-col"
      ariaLabel={t('shareProfileAria')}
    >
      <ModalHeader title={t('shareProfile')} onClose={onClose} />

      <div className="px-4 py-4 sm:px-6">
        <div className="rounded-xl border border-gray-300 bg-gray-50/80 p-6">
          <div className="flex flex-col items-center text-center">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className={SHARE_AVATAR_CLASS} />
            ) : (
              <Avatar
                initials={displayInitials}
                size="xl"
                className={cn(SHARE_AVATAR_CLASS, 'bg-gray-100 text-gray-600')}
              />
            )}

            <p className={cn(textStyles.cardTitle, 'mt-4 text-base')}>{displayName}</p>
            <p className={cn(textStyles.bodyMuted, 'mt-1 max-w-full truncate')}>{school}</p>

            <div
              ref={qrContainerRef}
              className="mt-6 rounded-xl border border-gray-300 bg-white p-4 shadow-sm"
              role="img"
              aria-label={t('qrCodeAria')}
            >
              <QRCode
                value={shareUrl}
                size={200}
                level="M"
                fgColor="#000000"
                bgColor="#FFFFFF"
                className="mx-auto h-auto max-w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid shrink-0 grid-cols-1 gap-2 border-t border-gray-300 px-4 py-3 sm:grid-cols-2 sm:px-6">
        <Button
          type="button"
          variant="primary"
          size="md"
          fullWidth
          onClick={() => void handleCopyLink()}
          leftIcon={<CopyIcon className="h-4 w-4" />}
        >
          {linkCopied ? t('linkCopied') : t('copyProfileLink')}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          fullWidth
          onClick={() => void handleDownloadFlyer()}
          leftIcon={<DownloadIcon className="h-4 w-4" />}
        >
          {flyerDownloaded ? t('flyerDownloaded') : t('downloadProfileFlyer')}
        </Button>
      </div>
    </Modal>
  );
}
